import { forwardRef, useCallback } from "react"

// --- Lib ---
import { parseShortcutKeys } from "@/tiptap/lib/tiptap-utils"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

import {
  IMAGE_ALIGN_SHORTCUT_KEYS,
  useImageAlign,
} from "@/tiptap/ui/image-align-button"

import { Button } from "@/tiptap/ui-primitive/button"
import { Badge } from "@/tiptap/ui-primitive/badge"

export function ImageAlignShortcutBadge({
  align,
  shortcutKeys = IMAGE_ALIGN_SHORTCUT_KEYS[align]
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for setting image alignment in a Tiptap editor.
 *
 * For custom button implementations, use the `useImageAlign` hook instead.
 */
export const ImageAlignButton = forwardRef((
  {
    editor: providedEditor,
    align,
    text,
    extensionName,
    attributeName = "data-align",
    hideWhenUnavailable = false,
    onAligned,
    showShortcut = false,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const {
    isVisible,
    handleImageAlign,
    label,
    canAlign,
    isActive,
    Icon,
    shortcutKeys,
  } = useImageAlign({
    editor,
    align,
    extensionName,
    attributeName,
    hideWhenUnavailable,
    onAligned,
  })

  const handleClick = useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleImageAlign()
  }, [handleImageAlign, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type="button"
      disabled={!canAlign}
      variant="ghost"
      data-active-state={isActive ? "on" : "off"}
      data-disabled={!canAlign}
      role="button"
      tabIndex={-1}
      aria-label={label}
      aria-pressed={isActive}
      tooltip={label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}>
      {children ?? (
        <>
          <Icon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
          {showShortcut ? (
            <ImageAlignShortcutBadge align={align} shortcutKeys={shortcutKeys} />
          ) : null}
        </>
      )}
    </Button>
  );
})

ImageAlignButton.displayName = "ImageAlignButton"
